import { BiQuerySpec } from '../bi/query';
import { ApdmDueBasis } from './enums';
import { ApdmSendingPolicy } from './out-reaching';

export enum ApdmCampaignKind {
  WinBack = 'win_back',
  MaintenanceDue = 'maintenance_due',
  Promotion = 'promotion',
  Greeting = 'greeting',
}
export enum ApdmCampaignMode {
  OneShot = 'one_shot',
  Ongoing = 'ongoing',
}
export enum ApdmCampaignStatus {
  Draft = 'draft',
  Scheduled = 'scheduled',
  Reaching = 'reaching',
  Paused = 'paused',
  Reached = 'reached',
  Ended = 'ended',
}
export enum ApdmReachability {
  Known = 'known',
  Unknown = 'unknown',
  Unreachable = 'unreachable',
}
export enum ApdmSendState {
  Due = 'due',
  InFlight = 'in_flight',
  Settled = 'settled',
  Unconfirmed = 'unconfirmed',
}
export enum ApdmOutreachState {
  Pending = 'pending',
  Reached = 'reached',
  Replied = 'replied',
  Engaged = 'engaged',
  NoResponse = 'no_response',
  NotInterested = 'not_interested',
  DoNotContact = 'do_not_contact',
  Converted = 'converted',
}

export type ApdmCampaignCondition =
  | {
      field:
        | 'customerName'
        | 'customerPhone'
        | 'vehicleBrandModel'
        | 'plateNo'
        | 'vin';
      operator: 'equals' | 'contains';
      value: string;
    }
  | {
      field: 'visitCount' | 'lastServiceMileage';
      operator: 'gte' | 'lte';
      value: number;
    }
  | {
      field: 'lastVisitAt' | 'lastServiceAt' | 'nextDueAt';
      operator: 'before' | 'after';
      value: string;
    };
export type ApdmAudienceRule =
  | { kind: 'win_back'; idleMonths: number }
  | { kind: 'maintenance_due'; withinDays: number; overdueUpToDays: number }
  | { kind: 'all_reachable' }
  | { kind: 'custom'; conditions: ApdmCampaignCondition[] }
  /** Mirror of backend: the customers the user named, by plate. */
  | { kind: 'plates'; plates: string[] }
  /**
   * Mirror of backend: the customers a question about the store's data
   * picks. The app shows only `description`, the owner's words.
   */
  | { kind: 'query'; query: BiQuerySpec; description: string };
export type ApdmCampaignAudienceSource =
  | { kind: 'platform' }
  | { kind: 'imports'; importIds: string[] }
  | { kind: 'shop_imports' };
export interface ApdmCampaignWebpageLink {
  kind: 'link';
  url: string;
  title: string;
  desc?: string;
  thumbnailAttachmentId: string | null;
}
/** Mirror of backend `kApdmDefaultDailyCap`: first messages a day unless
 * the plan says otherwise. */
export const kApdmDefaultDailyCap = 500;
/** The store-time hours (`HH:mm`) a campaign sends in, each day. */
export interface ApdmCampaignDailyWindow {
  from: string;
  to: string;
}

const kApdmClock = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The window the backend accepts, or null: two clocks, end after start. */
export function parseApdmDailyWindow(
  value: unknown,
): ApdmCampaignDailyWindow | null {
  const row = value as Partial<Record<keyof ApdmCampaignDailyWindow, unknown>>;
  if (
    !row ||
    typeof row !== 'object' ||
    typeof row.from !== 'string' ||
    typeof row.to !== 'string' ||
    !kApdmClock.test(row.from) ||
    !kApdmClock.test(row.to) ||
    row.from >= row.to
  )
    return null;
  return { from: row.from, to: row.to };
}

export function apdmDailyWindowText(window: ApdmCampaignDailyWindow): string {
  return `${window.from} – ${window.to}`;
}

export function sameDailyWindow(
  left: ApdmCampaignDailyWindow,
  right: ApdmCampaignDailyWindow,
): boolean {
  return left.from === right.from && left.to === right.to;
}

export interface ApdmCampaignSchedule {
  startAt: string | null;
  endAt: string | null;
  dailyWindow: ApdmCampaignDailyWindow;
  dailyCap: number | null;
  /**
   * Set when a launch on the start day, before the window opened, chose to
   * send immediately: that day's sending opens here.
   */
  opensEarlyAt?: string | null;
}
export interface ApdmCampaignPolicy {
  followUpDelaysDays: number[];
  lateFollowUpGraceDays: number;
  crossCampaignCooldownDays: number;
  conversationWindowDays: number;
  /** Where it reaches customers; null or absent = private chats only. */
  outReaching?: ApdmSendingPolicy | null;
}
export type ApdmCampaignPlaceholder =
  | 'customerName'
  | 'plateNo'
  | 'dueItems'
  | 'lastVisitDate'
  | 'storeName';
export enum ApdmCampaignVariantApplicability {
  Confirmed = 'confirmed',
  Estimated = 'estimated',
  All = 'all',
}
export interface ApdmCampaignVariantInput {
  text: string;
  requiredFields: ApdmCampaignPlaceholder[];
  applicability?: ApdmCampaignVariantApplicability | null;
}
export interface ApdmCampaignDefinition {
  title: string;
  kind: ApdmCampaignKind;
  mode: ApdmCampaignMode;
  audience: { rule: ApdmAudienceRule; sources: ApdmCampaignAudienceSource[] };
  content: {
    imageAttachmentIds: string[];
    couponLinkCard: boolean;
    webpageLink: ApdmCampaignWebpageLink | null;
  };
  couponCampaignId: string | null;
  posterTemplateAttachmentId: string | null;
  reviewEnabled: boolean;
  schedule: ApdmCampaignSchedule;
  policy: ApdmCampaignPolicy;
  variants: ApdmCampaignVariantInput[];
}
export type ApdmCreateCampaignInput = Pick<ApdmCampaignDefinition, 'kind'> &
  Partial<Omit<ApdmCampaignDefinition, 'kind'>>;
export interface ApdmCampaignVersionRef {
  campaignId: string;
  expectedVersion: number;
}
export interface ApdmUpdateCampaignDraftInput extends ApdmCampaignVersionRef {
  draft: ApdmCampaignDefinition;
}
export interface ApdmCampaignView extends ApdmCampaignDefinition {
  id: string;
  version: number;
  status: ApdmCampaignStatus;
}
export interface ApdmCampaignRecipientFacts {
  customerName: string | null;
  customerPhone: string | null;
  vehicleBrandModel: string | null;
  vin: string | null;
  lastVisitAt: string | Date | null;
  visitCount: number;
  lastServiceAt: string | Date | null;
  lastServiceMileage: number | null;
  nextDueAt: string | Date | null;
  nextDueItems: string[];
  nextDueBasis: ApdmDueBasis | null;
}
export type ApdmCampaignRecipientSource =
  | { kind: 'platform' }
  | { kind: 'import'; importId: string };
export interface ApdmCampaignDailyRef extends ApdmCampaignVersionRef {
  nextRunAt: string;
}
export interface ApdmCampaignStateResult {
  campaignId: string;
  version: number;
  status: ApdmCampaignStatus;
  nextTick: ApdmCampaignDailyRef | null;
}
